"use client";

import { c as _c } from "react/compiler-runtime";
import { CustomImage } from "@repo/design-system/components/ui/custom-image";
import { CustomLink } from "@repo/design-system/components/ui/custom-link";
import { cn } from "@repo/design-system/utils/cn";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useLanguage } from "~/translations/loader/use-language";
import type { Job } from "~/types/job";

// Job IDs that should show the Events section
const EVENTS_JOB_IDS = ["225389", "222645", "222706", "222707", "222708", "222709", "227033", "227035", "222789"];
interface EventSlideProps {
  image: string;
  linkText: string;
  linkUrl: string;
  step: string;
  text: string;
}
const EventSlide = memo(t0 => {
  const $ = _c(16);
  const {
    step,
    text,
    linkText,
    linkUrl,
    image
  } = t0;
  let t1;
  if ($[0] !== image) {
    t1 = <div className="relative h-full w-full"><CustomImage className="object-cover object-top" fill={true} sizes="(min-width: 1280px) 640px, (min-width: 1024px) 50vw, 100vw" src={image} /></div>;
    $[0] = image;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== step) {
    t2 = <h3 className="mb-6 font-bold text-3xl lg:text-3xl [&_p]:mb-1" dangerouslySetInnerHTML={{
      __html: step
    }} />;
    $[2] = step;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== text) {
    t3 = <div className="font-light sm:text-lg [&_p]:mb-2" dangerouslySetInnerHTML={{
      __html: text
    }} />;
    $[4] = text;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== linkText || $[7] !== linkUrl) {
    t4 = <div><CustomLink blank={true} className="mt-6 mr-3 inline-block rounded-[14px] bg-primary px-8 py-2.5 font-bold text-white leading-8 transition-colors duration-300 hover:brightness-90 sm:text-lg md:w-auto md:py-2" href={linkUrl}>{linkText}</CustomLink></div>;
    $[6] = linkText;
    $[7] = linkUrl;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t3 || $[11] !== t4) {
    t5 = <div className="bg-[#f7e8da] text-center"><div className="flex h-full min-h-[315px] w-full flex-col p-6 text-start sm:min-h-[260px] lg:p-10">{t2}{t3}{t4}</div></div>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t1 || $[14] !== t5) {
    t6 = <div className="grid h-full w-full auto-cols-fr grid-flow-row grid-rows-[1fr,3fr] xs:grid-rows-[1fr,2fr] text-foreground sm:grid-rows-[2fr,3fr] md:grid-rows-[1fr,1fr] lg:grid-flow-col lg:grid-rows-1 xl:min-h-[356px]">{t1}{t5}</div>;
    $[13] = t1;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
});
EventSlide.displayName = "EventSlide";
interface EventsEconProps {
  job: Job;
}
export default function EventsEcon(t0) {
  const $ = _c(35);
  const {
    job
  } = t0;
  const t = useLanguage();
  const [activeSlide, setActiveSlide] = useState(0);
  let t1;
  if ($[0] !== job.data.idClient) {
    t1 = EVENTS_JOB_IDS.includes(job.data.idClient);
    $[0] = job.data.idClient;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const shouldShow = t1;
  let t2;
  if ($[2] !== t.econ_events_event1_HL || $[3] !== t.econ_events_event1_linkText || $[4] !== t.econ_events_event1_text) {
    t2 = {
      step: t.econ_events_event1_HL,
      text: t.econ_events_event1_text,
      linkText: t.econ_events_event1_linkText,
      linkUrl: "https://www.careerlunch.com/companies/eon-inhouse-consulting-de",
      image: "images/jobad/econ/event_econ_careerlunch.webp"
    };
    $[2] = t.econ_events_event1_HL;
    $[3] = t.econ_events_event1_linkText;
    $[4] = t.econ_events_event1_text;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t.econ_events_event2_HL || $[7] !== t.econ_events_event2_linkText || $[8] !== t.econ_events_event2_text) {
    t3 = {
      step: t.econ_events_event2_HL,
      text: t.econ_events_event2_text,
      linkText: t.econ_events_event2_linkText,
      linkUrl: "mailto:Econ-CareerConnect@eon.com",
      image: "images/jobad/econ/event_career_connect.webp"
    };
    $[6] = t.econ_events_event2_HL;
    $[7] = t.econ_events_event2_linkText;
    $[8] = t.econ_events_event2_text;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t2 || $[11] !== t3) {
    t4 = [t2, t3];
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const steps = t4;
  let t5;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = index => {
      setActiveSlide(index);
    };
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const goToSlide = t5;
  let t6;
  let t7;
  if ($[14] !== shouldShow || $[15] !== steps.length) {
    t6 = () => {
      if (!shouldShow) {
        return;
      }
      const interval = setInterval(() => {
        setActiveSlide(prev => (prev + 1) % steps.length);
      }, 10000);
      return () => clearInterval(interval);
    };
    t7 = [shouldShow, steps.length];
    $[14] = shouldShow;
    $[15] = steps.length;
    $[16] = t6;
    $[17] = t7;
  } else {
    t6 = $[16];
    t7 = $[17];
  }
  useEffect(t6, t7);
  if (!shouldShow) {
    return null;
  }
  let t8;
  if ($[18] !== t.econ_events_HL) {
    t8 = <h2 className="mb-8 text-center font-bold text-4xl text-white xl:text-5xl">{t.econ_events_HL}</h2>;
    $[18] = t.econ_events_HL;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t.econ_events_intro) {
    t9 = <div className="mx-auto mb-12 w-5/6 text-center text-lg text-white lg:text-xl"><p data-source="lc">{t.econ_events_intro}</p></div>;
    $[20] = t.econ_events_intro;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== activeSlide || $[23] !== steps) {
    t10 = <div className="relative"><div className="flex w-full overflow-hidden">{steps.map((step, slideIndex) => <div className="float-left -mr-[100%] flex h-auto w-full transition-transform duration-700" key={slideIndex} style={{
          transform: `translateX(${(slideIndex - activeSlide) * 100}%)`,
          visibility: slideIndex === activeSlide ? "visible" : "hidden"
        }}><EventSlide {...step} /></div>)}</div></div>;
    $[22] = activeSlide;
    $[23] = steps;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t8 || $[27] !== t9) {
    t11 = <div className="inner-container grid h-full justify-center">{t8}{t9}{t10}</div>;
    $[25] = t10;
    $[26] = t8;
    $[27] = t9;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== activeSlide || $[30] !== steps) {
    t12 = <div className="mx-auto mt-5 flex h-full w-full justify-center">{steps.map((_, slideIndex_0) => <button aria-label={`Go to slide ${slideIndex_0 + 1}`} className={cn(slideIndex_0 === activeSlide ? "opacity-100" : "opacity-20", "mx-2 h-3 w-10 cursor-pointer bg-white")} key={slideIndex_0} onClick={() => goToSlide(slideIndex_0)} type="button" />)}</div>;
    $[29] = activeSlide;
    $[30] = steps;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t11 || $[33] !== t12) {
    t13 = <section className="outer-container bg-gradient-to-tr from-[#ea1b0a] to-[#00375c] py-10 md:py-20 print:hidden" id="events">{t11}{t12}</section>;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  return t13;
}